import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { Icon } from "@azim-crm/icons";
import { Button } from "../Button/Button.js";
import { Select } from "../Select/Select.js";
import styles from "./Table.module.css";
const MIN_COLUMN_WIDTH = 64;
const DEFAULT_COLUMN_WIDTH = 160;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
function parseColumnWidth(width) {
    if (typeof width === "number")
        return width;
    if (typeof width === "string") {
        const parsed = parseInt(width, 10);
        if (!Number.isNaN(parsed))
            return parsed;
    }
    return DEFAULT_COLUMN_WIDTH;
}
/**
 * Tabela densa do Azim CRM.
 * Cabeçalho sticky, scroll horizontal, zebra hover.
 * Linhas clicáveis: use TableRow com onClick.
 */
export function Table({ columns, children, className, ariaLabel, pagination }) {
    const hasResizableColumn = useMemo(() => columns.some((col) => col.resizable), [columns]);
    const [colWidths, setColWidths] = useState(() => Object.fromEntries(columns.map((col, i) => [i, parseColumnWidth(col.width)])));
    // Em modo redimensionável, a tabela recebe largura total explícita (soma das
    // colunas) para que `table-layout: fixed` honre cada largura em px — sem isso,
    // `width: 100%` reparte/clampa proporcionalmente e o arraste não se reflete.
    // `minWidth: 100%` mantém o preenchimento quando a soma é menor que o container.
    const totalWidth = useMemo(() => hasResizableColumn
        ? columns.reduce((sum, _col, i) => sum + (colWidths[i] ?? DEFAULT_COLUMN_WIDTH), 0)
        : undefined, [hasResizableColumn, columns, colWidths]);
    // Redimensionamento via pointer capture: uma vez pressionada a alça, todos os
    // `pointermove`/`pointerup` são roteados para ela mesmo que o cursor saia dos
    // seus limites ou passe sobre a coluna vizinha (mais robusto que listener em
    // `window` e imune à sobreposição do `<th>` adjacente).
    const startResize = (index, handle, pointerId, clientX) => {
        const startWidth = colWidths[index] ?? DEFAULT_COLUMN_WIDTH;
        // Feature-detect: jsdom (testes) e navegadores antigos podem não implementar
        // pointer capture; nesses casos seguimos sem ele (os listeners na alça ainda
        // recebem os eventos disparados diretamente sobre ela).
        if (typeof handle.setPointerCapture === "function") {
            handle.setPointerCapture(pointerId);
        }
        const onMove = (ev) => {
            const next = Math.max(MIN_COLUMN_WIDTH, startWidth + (ev.clientX - clientX));
            setColWidths((prev) => ({ ...prev, [index]: next }));
        };
        const onUp = () => {
            if (typeof handle.releasePointerCapture === "function") {
                handle.releasePointerCapture(pointerId);
            }
            handle.removeEventListener("pointermove", onMove);
            handle.removeEventListener("pointerup", onUp);
            handle.removeEventListener("pointercancel", onUp);
        };
        handle.addEventListener("pointermove", onMove);
        handle.addEventListener("pointerup", onUp);
        handle.addEventListener("pointercancel", onUp);
    };
    return (_jsxs("div", { className: [styles.wrapper, className ?? ""].filter(Boolean).join(" "), children: [_jsxs("table", { className: [styles.table, hasResizableColumn ? styles.tableFixed : ""].filter(Boolean).join(" "), role: "table", ...(totalWidth !== undefined ? { style: { width: totalWidth, minWidth: "100%" } } : {}), ...(ariaLabel !== undefined ? { "aria-label": ariaLabel } : {}), children: [_jsx("thead", { children: _jsx("tr", { children: columns.map((col, i) => {
                                const thCls = [
                                    styles.th,
                                    col.align === "right" ? styles.thRight : "",
                                    col.align === "center" ? styles.thCenter : "",
                                    col.resizable ? styles.thResizable : "",
                                    col.className ?? "",
                                ]
                                    .filter(Boolean)
                                    .join(" ");
                                // Em modo fixed, TODA coluna recebe largura px explícita (via
                                // colWidths[i], já inicializado a partir de col.width) para que o
                                // layout fixo honre as larguras e o redimensionamento seja visível.
                                const colStyle = hasResizableColumn
                                    ? { width: colWidths[i] }
                                    : col.width
                                        ? { width: col.width }
                                        : {};
                                const ariaSort = !col.sortable
                                    ? undefined
                                    : col.sortDirection === "asc"
                                        ? "ascending"
                                        : col.sortDirection === "desc"
                                            ? "descending"
                                            : "none";
                                const labelContent = col.srOnlyLabel
                                    ? _jsx("span", { className: styles.srOnly, children: col.label })
                                    : col.label;
                                return (_jsxs("th", { scope: "col", className: thCls, style: colStyle, ...(ariaSort !== undefined ? { "aria-sort": ariaSort } : {}), children: [col.sortable ? (_jsxs("button", { type: "button", className: styles.sortButton, onClick: col.onSort, children: [labelContent, _jsx(Icon, { name: "sort", size: 12, className: [
                                                        styles.sortIcon,
                                                        col.sortDirection === "asc" ? styles.sortIconAsc : "",
                                                        col.sortDirection === "desc" ? styles.sortIconDesc : "",
                                                    ].filter(Boolean).join(" ") })] })) : (labelContent), col.resizable && (_jsx("span", { role: "separator", "aria-orientation": "vertical", "aria-label": `Redimensionar coluna ${col.label}`, className: styles.resizeHandle, onPointerDown: (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                startResize(i, e.currentTarget, e.pointerId, e.clientX);
                                            } }))] }, i));
                            }) }) }), _jsx("tbody", { children: children })] }), pagination && _jsx(TablePaginationFooter, { pagination: pagination })] }));
}
function TablePaginationFooter({ pagination }) {
    const { page, pageSize, totalCount, pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS, onPageChange, onPageSizeChange, } = pagination;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    return (_jsxs("div", { className: styles.paginationFooter, children: [_jsxs("div", { className: styles.paginationPageSize, children: [_jsx(Select, { value: String(pageSize), options: pageSizeOptions.map((n) => ({ value: String(n), label: String(n) })), onChange: (value) => onPageSizeChange(Number(value)), ariaLabel: "Itens por p\u00E1gina" }), _jsx("span", { children: "por p\u00E1gina" })] }), _jsxs("div", { className: styles.paginationControls, children: [_jsxs("span", { children: ["P\u00E1gina ", page, " de ", totalPages] }), _jsx(Button, { type: "button", variant: "secondary", size: "sm", disabled: page <= 1, onClick: () => onPageChange(page - 1), children: "Anterior" }), _jsx(Button, { type: "button", variant: "secondary", size: "sm", disabled: page >= totalPages, onClick: () => onPageChange(page + 1), children: "Pr\u00F3xima" })] })] }));
}
/**
 * Linha de tabela do Azim CRM.
 * Quando onClick é passado, fica focável pelo teclado.
 */
export function TableRow({ children, onClick, className, interactiveContent = false }) {
    const cls = [styles.row, onClick ? styles.rowClickable : "", className ?? ""]
        .filter(Boolean)
        .join(" ");
    const handleKey = (e) => {
        // Só trata Enter/Espaço quando o evento se origina na própria linha —
        // nunca quando borbulha de um elemento interativo aninhado (checkbox,
        // botão, link dentro de uma TableCell), senão o Espaço do checkbox
        // aciona o onClick da linha em vez do toggle nativo do checkbox.
        if (e.target !== e.currentTarget)
            return;
        if (onClick && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onClick(e);
        }
    };
    return (_jsx("tr", { className: cls, onClick: onClick, onKeyDown: onClick ? handleKey : undefined, tabIndex: onClick ? 0 : undefined, role: onClick && !interactiveContent ? "button" : undefined, children: children }));
}
/**
 * Célula de tabela com padding padrão.
 */
export function TableCell({ children, align, className, style }) {
    const tdStyle = {
        textAlign: align,
        ...style,
    };
    return (_jsx("td", { className: [styles.td, className ?? ""].filter(Boolean).join(" "), style: tdStyle, children: children }));
}
//# sourceMappingURL=Table.js.map